/**
 * useSubtabNavigation
 *
 * Enables the Command Palette (and other callers) to deep-link into a
 * specific tab within a page.  The flow is:
 *
 *   1. Command Palette sets `localStorage('pending_subtab', tabValue)`
 *      and navigates to the parent page.
 *   2. The page mounts (or is already mounted) and this hook fires.
 *   3. The hook reads + clears `pending_subtab` and calls `setActiveTab`.
 *   4. It also listens for the `navigate-to-subtab` custom event so
 *      already-mounted pages react instantly.
 */

import { useEffect } from 'react';

const STORAGE_KEY = 'pending_subtab';

/**
 * Call this inside any page that has an `activeTab` + `setActiveTab`.
 *
 * @param setActiveTab  state setter (or any callback) that switches tabs
 * @param validTabs     optional allowlist — if provided, the hook will
 *                      only apply tab values that appear in this array
 */
export function useSubtabNavigation(
  setActiveTab: (tab: string) => void,
  validTabs?: readonly string[],
) {
  useEffect(() => {
    // ── 1. Check localStorage on mount ──
    const pending = localStorage.getItem(STORAGE_KEY);
    if (pending) {
      localStorage.removeItem(STORAGE_KEY);
      if (!validTabs || validTabs.includes(pending)) {
        setActiveTab(pending);
      }
    }

    // ── 2. Listen for live event (page already mounted) ──
    const handler = (e: Event) => {
      const tab = (e as CustomEvent<string>).detail;
      if (tab && (!validTabs || validTabs.includes(tab))) {
        setActiveTab(tab);
      }
    };
    window.addEventListener('navigate-to-subtab', handler);
    return () => window.removeEventListener('navigate-to-subtab', handler);
  }, [setActiveTab, validTabs]);
}
